import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminWs } from '../context/AdminWsContext';
import { fmtDate } from '../utils/formatDate';
import {
  RefreshCw, Search, Activity, Trash2,
  CheckCircle, AlertCircle, Clock, X, CreditCard, Layers,
  Plus, Lock, Unlock, Copy, Zap, Banknote, Save, MapPin, Monitor,
  Settings, Info,
} from 'lucide-react';
import { MapPicker } from '../components/MapPicker';

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n || 0);

const PRICE_FIELDS = [
  { key: 'price_print_simplex', label: 'Ч/Б печать одна сторона',   short: 'Печать 1ст',  placeholder: '500'  },
  { key: 'price_print_duplex',  label: 'Ч/Б печать двухсторон',     short: 'Печать 2ст',  placeholder: '1500' },
  { key: 'price_scan_simplex',  label: 'Сканирование одна сторона', short: 'Скан 1ст',    placeholder: '500'  },
  { key: 'price_scan_duplex',   label: 'Сканирование двухсторон',   short: 'Скан 2ст',    placeholder: '500'  },
];

const SOURCE_LABELS = {
  phone:    'С телефона',
  phone_tg: 'С Telegram',
  usb:      'USB флешка',
  scan:     'Сканер / Копия',
};

/* ── Toast ───────────────────────────────────────────────────────────────── */
const Toast = ({ message, type = 'info', onClose }) => (
  <div className={`toast toast-${type}`}>
    {type === 'success' && <CheckCircle size={14} style={{ color: 'var(--green)',  flexShrink: 0 }} />}
    {type === 'error'   && <AlertCircle size={14} style={{ color: 'var(--red)',    flexShrink: 0 }} />}
    {type === 'info'    && <Clock       size={14} style={{ color: 'var(--brand)',  flexShrink: 0 }} />}
    <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>{message}</span>
    <button className="btn-icon" style={{ width: '24px', height: '24px', border: 'none' }} onClick={onClose}>
      <X size={12} />
    </button>
  </div>
);

/* ── Device status badge ─────────────────────────────────────────────────── */
const DeviceStatusBadge = ({ device }) => {
  if (device.is_blocked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span className="dot" style={{ background: 'var(--red)' }} />
        <span style={{ fontSize: '13px', color: 'var(--red)' }}>Заблокирован</span>
      </div>
    );
  }
  if (device.status === 'pending') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span className="dot" style={{ background: 'var(--text-3)' }} />
        <span style={{ fontSize: '13px', color: 'var(--text-3)' }}>Ожидание</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
      <span className={`dot ${device.is_online ? 'dot-online' : 'dot-offline'}`} />
      <span style={{ fontSize: '13px', color: device.is_online ? 'var(--green)' : 'var(--text-2)' }}>
        {device.is_online ? 'Онлайн' : 'Оффлайн'}
      </span>
    </div>
  );
};

/* ── Строка значения с кнопкой копирования (AnyDesk ID/пароль) ──────────── */
const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', fontSize: '12px', gap: '8px' }}>
      <span className="text-muted" style={{ flexShrink: 0 }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        <span className="mono" style={{ fontWeight: '600', wordBreak: 'break-all' }}>{value}</span>
        <button
          className="btn-icon"
          title={copied ? 'Скопировано' : 'Копировать'}
          style={{ width: '22px', height: '22px', flexShrink: 0, color: copied ? 'var(--green)' : 'var(--text-3)' }}
          onClick={copy}
        >
          <Copy size={11} />
        </button>
      </span>
    </div>
  );
};

/* ── Простая строка «ключ → значение» ───────────────────────────────────── */
const InfoRow = ({ k, v, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px', fontSize: '12px', gap: '8px' }}>
    <span className="text-muted" style={{ flexShrink: 0 }}>{k}</span>
    <span style={{ fontWeight: strong ? '600' : '500', textAlign: 'right', wordBreak: 'break-all' }}>{v}</span>
  </div>
);

const SectionLabel = ({ children, style }) => (
  <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', ...style }}>
    {children}
  </p>
);

/* ── Liquid-glass переключатель ──────────────────────────────────────────── */
const Toggle = ({ checked, onChange, disabled }) => (
  <label className="ios-switch">
    <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
    <span className="ios-track"><span className="ios-knob" /></span>
  </label>
);

/* ════════════════════════════════════════════════════════════════════════
   ВКЛАДКА «ИНФОРМАЦИЯ» — статистика 30д, данные устройства, цены (просмотр)
   ════════════════════════════════════════════════════════════════════════ */
const InfoTab = ({ device }) => {
  const { fetchAuthorized, isAdmin } = useAuth();
  const [bySourceMode, setBySourceMode] = useState(null);

  useEffect(() => {
    fetchAuthorized(`/api/dashboard/stats?period=30d&device_id=${device.device_id}`)
      .then(r => r.json())
      .then(d => { if (d.success) setBySourceMode(d.by_source_mode || []); })
      .catch(() => setBySourceMode([]));
  }, [device.device_id, fetchAuthorized]);

  const grouped = {};
  (bySourceMode || []).forEach(row => {
    const src  = row.source || 'phone';
    const mode = row.is_duplex ? 'duplex' : 'simplex';
    if (!grouped[src]) grouped[src] = {};
    grouped[src][mode] = { orders: row.orders, sheets: row.sheets };
  });

  const infoRows = [
    ['Имя (admin)',      device.name],
    ['Имя машины',       device.machine_name],
    ['Местоположение',  device.location],
    ['OS',              device.os_version],
    ['Версия лаунчера', device.app_version],
    ['Версия UI',       device.ui_version],
    ['Принтер',         device.printer_name],
    ['Сканер',          device.scanner_status],
    ['Зарегистрирован', fmtDate(device.registered_at)],
    ['Последняя связь', fmtDate(device.last_seen)],
  ];

  return (
    <>
      {/* Сводка за 30 дней */}
      <div className="dev-stat-grid">
        <div className="dev-stat-tile">
          <div className="dev-stat-val">{fmt(device.revenue_30d)}<span className="text-dim" style={{ fontSize: '11px', fontWeight: 400 }}> UZS</span></div>
          <div className="dev-stat-lbl">Выручка 30д</div>
        </div>
        <div className="dev-stat-tile">
          <div className="dev-stat-val">{fmt(device.orders_30d)}</div>
          <div className="dev-stat-lbl">Заказов 30д</div>
        </div>
        <div className="dev-stat-tile">
          <div className="dev-stat-val">{fmt(device.sheets_30d)}<span className="text-dim" style={{ fontSize: '11px', fontWeight: 400 }}> л.</span></div>
          <div className="dev-stat-lbl">Листов 30д</div>
        </div>
      </div>

      {/* Принтер / бумага */}
      <div className="settings-block">
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', gap: '8px', marginBottom: '7px' }}>
          <span className="text-muted">🖨 Принтер</span>
          <span style={{ fontWeight: '600', color: (device.printer_status === 'ok' || device.printer_status === 'Ready') ? 'var(--green)' : device.printer_status ? 'var(--yellow)' : 'var(--text-3)' }}>
            {device.printer_status || '—'}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', gap: '8px' }}>
          <span className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Layers size={10} /> Бумага в лотке
          </span>
          <span style={{ fontWeight: '600', color: device.paper_low ? 'var(--yellow)' : 'var(--text-1)' }}>
            {device.paper_count > 0 ? `${fmt(device.paper_count)} л.${device.paper_low ? ' ⚠' : ''}` : '—'}
          </span>
        </div>
      </div>

      {/* Данные устройства */}
      <div className="settings-block">
        <SectionLabel>Устройство</SectionLabel>
        {infoRows.map(([k, v]) => v ? <InfoRow key={k} k={k} v={v} /> : null)}

        {/* AnyDesk — поля приходят только полному админу */}
        {isAdmin && (device.anydesk_id || device.anydesk_password) && (
          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Monitor size={10} /> AnyDesk
            </div>
            {device.anydesk_id && <CopyRow label="ID" value={device.anydesk_id} />}
            {device.anydesk_password && <CopyRow label="Пароль" value={device.anydesk_password} />}
          </div>
        )}
      </div>

      {/* Цены киоска (просмотр) */}
      <div className="settings-block">
        <SectionLabel>Цены киоска</SectionLabel>
        {PRICE_FIELDS.map(({ key, short }) => (
          <InfoRow key={key} k={short} v={device[key] ? `${fmt(device[key])} UZS` : '—'} strong />
        ))}
      </div>

      {/* Разбивка по разделам и режиму */}
      <div className="settings-block">
        <SectionLabel>Разделы и режим — 30 дней</SectionLabel>
        {bySourceMode === null ? (
          <p style={{ fontSize: '12px' }} className="text-dim">Загрузка...</p>
        ) : bySourceMode.length === 0 ? (
          <p style={{ fontSize: '12px' }} className="text-dim">Нет оплаченных заказов за 30 дней</p>
        ) : (
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Раздел', 'Режим', 'Заказов', 'Листов'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0 8px 6px 0', color: 'var(--text-3)', fontWeight: '600', fontSize: '11px', background: 'transparent', border: 'none', textTransform: 'none', letterSpacing: 0 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(SOURCE_LABELS).flatMap(([src, label]) => {
                const modes = grouped[src];
                if (!modes) return [];
                return [
                  modes.simplex && (
                    <tr key={`${src}-s`}>
                      <td style={{ padding: '3px 8px 3px 0', color: 'var(--text-1)', fontWeight: '500', border: 'none' }}>{label}</td>
                      <td style={{ padding: '3px 8px 3px 0', color: 'var(--text-2)', border: 'none' }}>Одностор.</td>
                      <td style={{ padding: '3px 8px 3px 0', color: 'var(--text-2)', border: 'none' }}>{fmt(modes.simplex.orders)}</td>
                      <td style={{ padding: '3px 0', fontWeight: '600', border: 'none' }}>{fmt(modes.simplex.sheets)} л.</td>
                    </tr>
                  ),
                  modes.duplex && (
                    <tr key={`${src}-d`}>
                      <td style={{ padding: '3px 8px 3px 0', color: 'var(--text-1)', fontWeight: '500', border: 'none' }}>{!modes.simplex ? label : ''}</td>
                      <td style={{ padding: '3px 8px 3px 0', color: 'var(--brand)', border: 'none' }}>Двустор.</td>
                      <td style={{ padding: '3px 8px 3px 0', color: 'var(--text-2)', border: 'none' }}>{fmt(modes.duplex.orders)}</td>
                      <td style={{ padding: '3px 0', fontWeight: '600', border: 'none' }}>{fmt(modes.duplex.sheets)} л.</td>
                    </tr>
                  ),
                ].filter(Boolean);
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   ВКЛАДКА «НАСТРОЙКИ» — все действия (по правам)
   ════════════════════════════════════════════════════════════════════════ */
const SettingsTab = ({ device, handlers }) => {
  const { isAdmin, can } = useAuth();
  const {
    onPaymentToggle, onBlock, onUnblock, onResetWs,
    onSaveInfo, onSavePrices, onSaveAnyDesk, onSaveLocation, onRequestDelete,
  } = handlers;

  const isPending = device.status === 'pending';

  // — Имя / местоположение —
  const [infoName, setInfoName]   = useState(device.name || '');
  const [infoLoc, setInfoLoc]     = useState(device.location || '');
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoError, setInfoError]   = useState('');
  const saveInfo = async () => {
    if (!infoName.trim()) { setInfoError('Введите название киоска'); return; }
    setInfoSaving(true); setInfoError('');
    try { await onSaveInfo(device.device_id, { name: infoName.trim(), location: infoLoc.trim() }); }
    catch (err) { setInfoError(err.message || 'Ошибка сохранения'); }
    finally { setInfoSaving(false); }
  };

  // — Оплата —
  const [paymentBusy, setPaymentBusy] = useState(false);
  const togglePayment = async () => {
    setPaymentBusy(true);
    try { await onPaymentToggle(device.device_id); } finally { setPaymentBusy(false); }
  };

  // — Блокировка —
  const [blockBusy, setBlockBusy] = useState(false);
  const toggleBlock = async () => {
    setBlockBusy(true);
    try {
      if (device.is_blocked) await onUnblock(device.device_id);
      else                   await onBlock(device.device_id);
    } finally { setBlockBusy(false); }
  };

  // — Сброс WS —
  const [resetBusy, setResetBusy] = useState(false);
  const resetWs = async () => {
    setResetBusy(true);
    try { await onResetWs(device.device_id); } finally { setResetBusy(false); }
  };

  // — Цены —
  const [priceValues, setPriceValues] = useState(() =>
    Object.fromEntries(PRICE_FIELDS.map(f => [f.key, device[f.key] ?? '']))
  );
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceError, setPriceError]   = useState('');
  const savePrices = async () => {
    const payload = {};
    for (const { key, label } of PRICE_FIELDS) {
      const v = parseInt(priceValues[key], 10);
      if (!Number.isFinite(v) || v <= 0) { setPriceError(`Укажите корректную цену: ${label}`); return; }
      payload[key] = v;
    }
    setPriceSaving(true); setPriceError('');
    try { await onSavePrices(device.device_id, payload); }
    catch (err) { setPriceError(err.message || 'Ошибка сохранения цен'); }
    finally { setPriceSaving(false); }
  };

  // — AnyDesk —
  const [anydeskId, setAnydeskId]   = useState(device.anydesk_id || '');
  const [anydeskPwd, setAnydeskPwd] = useState(device.anydesk_password || '');
  const [anydeskSaving, setAnydeskSaving] = useState(false);
  const [anydeskError, setAnydeskError]   = useState('');
  const saveAnyDesk = async () => {
    setAnydeskSaving(true); setAnydeskError('');
    try { await onSaveAnyDesk(device.device_id, { anydesk_id: anydeskId.trim(), anydesk_password: anydeskPwd.trim() }); }
    catch (err) { setAnydeskError(err.message || 'Ошибка сохранения'); }
    finally { setAnydeskSaving(false); }
  };

  // — Локация —
  const [coords, setCoords] = useState(
    device.lat != null && device.lng != null ? { lat: device.lat, lng: device.lng } : null
  );
  const [locSaving, setLocSaving] = useState(false);
  const [locError, setLocError]   = useState('');
  const saveLocation = async () => {
    setLocSaving(true); setLocError('');
    try { await onSaveLocation(device.device_id, coords); }
    catch (err) { setLocError(err.message || 'Ошибка сохранения локации'); }
    finally { setLocSaving(false); }
  };
  const hasAuto = device.auto_lat != null && device.auto_lng != null;

  const labelStyle = { fontSize: '12px', color: 'var(--text-3)', display: 'block', marginBottom: '5px' };

  return (
    <>
      {/* Имя и местоположение — только полный админ */}
      {isAdmin && (
        <div className="settings-block">
          <div className="settings-block-title" style={{ marginBottom: '12px' }}><Info size={14} style={{ color: 'var(--brand)' }} /> Имя и местоположение</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div>
              <label style={labelStyle}>Название киоска</label>
              <input value={infoName} onChange={e => setInfoName(e.target.value)} placeholder="ТЦ Афросиаб — 1 этаж" style={{ width: '100%' }} />
            </div>
            <div>
              <label style={labelStyle}>Местоположение</label>
              <input value={infoLoc} onChange={e => setInfoLoc(e.target.value)} placeholder="Адрес или описание места" style={{ width: '100%' }} />
            </div>
          </div>
          {infoError && <p style={{ fontSize: '12px', color: 'var(--red)', marginBottom: '10px' }}>{infoError}</p>}
          <button className="btn btn-primary btn-sm" disabled={infoSaving} onClick={saveInfo}>
            <Save size={12} /> {infoSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      )}

      {/* Оплата */}
      {!isPending && can('act_payment_toggle') && (
        <div className="settings-block">
          <div className="settings-block-row">
            <div>
              <div className="settings-block-title"><CreditCard size={14} style={{ color: 'var(--brand)' }} /> Приём оплаты</div>
              <div className="settings-block-desc">Когда выключено — киоск печатает бесплатно (без QR оплаты).</div>
            </div>
            <Toggle checked={!!device.payment_enabled} disabled={paymentBusy} onChange={togglePayment} />
          </div>
        </div>
      )}

      {/* Блокировка — только полный админ */}
      {isAdmin && (
        <div className="settings-block">
          <div className="settings-block-row">
            <div>
              <div className="settings-block-title">
                {device.is_blocked ? <Lock size={14} style={{ color: 'var(--red)' }} /> : <Unlock size={14} style={{ color: 'var(--green)' }} />}
                Доступ устройства
              </div>
              <div className="settings-block-desc">
                {device.is_blocked
                  ? 'Устройство заблокировано: API и WS отклоняются.'
                  : 'Устройство активно. Выключите, чтобы заблокировать весь доступ.'}
              </div>
            </div>
            <Toggle checked={!device.is_blocked} disabled={blockBusy} onChange={toggleBlock} />
          </div>
        </div>
      )}

      {/* Сброс WS */}
      {!isPending && can('act_reset_ws') && (
        <div className="settings-block">
          <div className="settings-block-row">
            <div>
              <div className="settings-block-title"><Zap size={14} style={{ color: 'var(--blue, #3b82f6)' }} /> Сброс WS-соединения</div>
              <div className="settings-block-desc">Через HTTP-флаг — сработает даже если связь зависла.</div>
            </div>
            <button className="btn btn-sm" disabled={resetBusy} onClick={resetWs}>
              <Zap size={12} /> {resetBusy ? 'Отправлено' : 'Сбросить'}
            </button>
          </div>
        </div>
      )}

      {/* Цены */}
      {can('act_prices') && (
        <div className="settings-block">
          <div className="settings-block-title" style={{ marginBottom: '12px' }}><Banknote size={14} style={{ color: 'var(--brand)' }} /> Цены киоска</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            {PRICE_FIELDS.map(({ key, label, placeholder }) => (
              <div key={key}>
                <label style={labelStyle}>{label} (UZS)</label>
                <input
                  type="number" min="1"
                  value={priceValues[key]}
                  onChange={e => setPriceValues(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={placeholder}
                  style={{ width: '100%' }}
                />
              </div>
            ))}
          </div>
          {priceError && <p style={{ fontSize: '12px', color: 'var(--red)', marginBottom: '10px' }}>{priceError}</p>}
          <button className="btn btn-primary btn-sm" disabled={priceSaving} onClick={savePrices}>
            <Save size={12} /> {priceSaving ? 'Сохранение...' : 'Сохранить цены'}
          </button>
        </div>
      )}

      {/* AnyDesk — только полный админ */}
      {isAdmin && (
        <div className="settings-block">
          <div className="settings-block-title" style={{ marginBottom: '4px' }}><Monitor size={14} style={{ color: 'var(--brand)' }} /> AnyDesk</div>
          <div className="settings-block-desc" style={{ marginBottom: '12px' }}>Данные удалённого доступа (справочно).</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div>
              <label style={labelStyle}>AnyDesk ID</label>
              <input value={anydeskId} onChange={e => setAnydeskId(e.target.value)} placeholder="123 456 789" style={{ width: '100%', fontFamily: 'Consolas, monospace' }} />
            </div>
            <div>
              <label style={labelStyle}>Пароль</label>
              <input value={anydeskPwd} onChange={e => setAnydeskPwd(e.target.value)} placeholder="Пароль доступа" style={{ width: '100%', fontFamily: 'Consolas, monospace' }} />
            </div>
          </div>
          {anydeskError && <p style={{ fontSize: '12px', color: 'var(--red)', marginBottom: '10px' }}>{anydeskError}</p>}
          <button className="btn btn-primary btn-sm" disabled={anydeskSaving} onClick={saveAnyDesk}>
            <Save size={12} /> {anydeskSaving ? 'Сохранение...' : 'Сохранить AnyDesk'}
          </button>
        </div>
      )}

      {/* Локация — только полный админ */}
      {isAdmin && (
        <div className="settings-block">
          <div className="settings-block-title" style={{ marginBottom: '4px' }}><MapPin size={14} style={{ color: 'var(--brand)' }} /> Локация на карте</div>
          <div className="settings-block-desc" style={{ marginBottom: '12px' }}>
            Точка, где киоск должен стоять. Если фактическая позиция (Wi-Fi) уедет — придёт алерт в Telegram.
          </div>
          <MapPicker value={coords} onChange={setCoords} height={220} />
          {hasAuto && (
            <p style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '10px' }}>
              📡 Факт. позиция (Wi-Fi):{' '}
              <span className="mono">{device.auto_lat?.toFixed(6)}, {device.auto_lng?.toFixed(6)}</span>{' · '}
              <button
                type="button" className="btn-icon"
                title="Поставить пин в фактическую позицию"
                style={{ width: 'auto', padding: '2px 8px', fontSize: '11px', display: 'inline-flex' }}
                onClick={() => setCoords({ lat: +device.auto_lat.toFixed(6), lng: +device.auto_lng.toFixed(6) })}
              >
                использовать её
              </button>
            </p>
          )}
          {locError && <p style={{ fontSize: '12px', color: 'var(--red)', marginTop: '10px' }}>{locError}</p>}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary btn-sm" disabled={locSaving} onClick={saveLocation}>
              <Save size={12} /> {locSaving ? 'Сохранение...' : 'Сохранить точку'}
            </button>
            {coords && (
              <button className="btn btn-sm" disabled={locSaving} onClick={() => setCoords(null)}>
                Очистить
              </button>
            )}
          </div>
        </div>
      )}

      {/* Удаление — только полный админ */}
      {isAdmin && (
        <div className="settings-block">
          <div className="settings-block-row">
            <div>
              <div className="settings-block-title" style={{ color: 'var(--red)' }}><Trash2 size={14} /> Удалить киоск</div>
              <div className="settings-block-desc">Удалит устройство, все его заказы и сессии. Необратимо.</div>
            </div>
            <button className="btn btn-danger btn-sm" onClick={() => onRequestDelete(device)}>
              <Trash2 size={12} /> Удалить
            </button>
          </div>
        </div>
      )}
    </>
  );
};

/* ── Единая модалка управления киоском ───────────────────────────────────── */
const DeviceModal = ({ device, handlers, onClose }) => {
  const { isAdmin, can } = useAuth();
  const [tab, setTab] = useState('info');

  // Какие настройки доступны текущей роли — если ни одной, вкладку прячем.
  const hasSettings = isAdmin
    || (device.status !== 'pending' && (can('act_payment_toggle') || can('act_reset_ws')))
    || can('act_prices');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="modal-overlay"
      style={{ overflowY: 'auto', padding: '24px 12px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card dev-modal">
        {/* Шапка */}
        <div className="dev-modal-head">
          <div style={{ minWidth: 0 }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px', wordBreak: 'break-word' }}>
              {device.name || device.machine_name || 'Без имени'}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <DeviceStatusBadge device={device} />
              <span className="mono text-dim" style={{ fontSize: '11px' }}>{device.device_id?.slice(0, 18)}</span>
            </div>
          </div>
          <button className="btn-icon" onClick={onClose} title="Закрыть" style={{ flexShrink: 0 }}>
            <X size={15} />
          </button>
        </div>

        {/* Вкладки */}
        <div className="dev-modal-tabs">
          <button className={`dev-tab ${tab === 'info' ? 'active' : ''}`} onClick={() => setTab('info')}>
            <Info size={14} /> Информация
          </button>
          {hasSettings && (
            <button className={`dev-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
              <Settings size={14} /> Настройки
            </button>
          )}
        </div>

        {/* Тело */}
        <div className="dev-modal-body">
          {tab === 'info'
            ? <InfoTab device={device} />
            : <SettingsTab device={device} handlers={handlers} />}
        </div>
      </div>
    </div>
  );
};

/* ── Имя киоска + бейдж профиля (общее для строки и мобильной карточки) ──── */
const DeviceTitle = ({ device }) => {
  const { isAdmin } = useAuth();
  return (
    <>
      <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>
        {device.name || device.machine_name || 'Без имени'}
      </div>
      {device.name && device.machine_name && (
        <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '2px' }}>
          {device.machine_name}
        </div>
      )}
      <div style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }} className="text-dim">
        <Activity size={9} />
        <span className="mono">{device.device_id?.slice(0, 14)}</span>
      </div>
      {device.location && (
        <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '1px' }}>
          📍 {device.location}
        </div>
      )}
      {/* Принадлежность профилю (видна только админу — у профиля все киоски свои) */}
      {isAdmin && (
        <div style={{ marginTop: '3px' }}>
          {device.profile_id ? (
            <span style={{
              fontSize: '10px', padding: '1px 7px', borderRadius: '20px',
              background: 'rgba(129,140,248,0.14)', color: 'var(--brand)', fontWeight: 600,
            }}>
              👤 {device.profile_display_name || device.profile_username}
            </span>
          ) : (
            <span style={{
              fontSize: '10px', padding: '1px 7px', borderRadius: '20px',
              background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)',
            }}>
              без профиля
            </span>
          )}
        </div>
      )}
    </>
  );
};

/* ── Кнопка «Управление» (открывает модалку) ─────────────────────────────── */
const ManageButton = ({ device, onOpen, mobile = false }) => (
  <button
    className="btn btn-sm"
    style={mobile ? { width: '100%', justifyContent: 'center' } : undefined}
    onClick={() => onOpen(device)}
  >
    <Settings size={13} /> Управление
  </button>
);

/* ── Строка таблицы (десктоп) ────────────────────────────────────────────── */
const DeviceRow = ({ device, onOpen }) => {
  const printerOk = device.printer_status === 'ok' || device.printer_status === 'Ready';
  const isPending = device.status === 'pending';

  return (
    <tr style={{ opacity: device.is_blocked ? 0.6 : 1 }}>
      <td><DeviceTitle device={device} /></td>

      <td>
        <DeviceStatusBadge device={device} />
        <div style={{ fontSize: '11px', marginTop: '2px' }} className="text-dim">{fmtDate(device.last_seen)}</div>
      </td>

      <td>
        {isPending ? (
          <span style={{ fontSize: '12px' }} className="text-dim">—</span>
        ) : (
          <>
            <span style={{ fontSize: '13px', color: printerOk ? 'var(--green)' : device.printer_status ? 'var(--yellow)' : 'var(--text-3)' }}>
              {device.printer_status || '—'}
            </span>
            <div style={{ fontSize: '11px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Layers size={9} style={{ color: device.paper_low ? 'var(--yellow)' : 'var(--text-3)', flexShrink: 0 }} />
              <span style={{ color: device.paper_low ? 'var(--yellow)' : 'var(--text-3)' }}>
                {device.paper_count > 0 ? `${fmt(device.paper_count)} л.` : '—'}
              </span>
            </div>
          </>
        )}
      </td>

      <td>
        {isPending ? (
          <span style={{ fontSize: '12px' }} className="text-dim">—</span>
        ) : (
          <>
            <span style={{ fontWeight: '600' }}>{fmt(device.revenue_30d)}</span>
            <span className="text-dim" style={{ fontSize: '11px' }}> UZS</span>
            <div className="text-dim" style={{ fontSize: '11px', marginTop: '1px' }}>
              {fmt(device.orders_30d)} заказов · {fmt(device.sheets_30d)} л.
            </div>
          </>
        )}
      </td>

      <td style={{ textAlign: 'right' }}>
        <ManageButton device={device} onOpen={onOpen} />
      </td>
    </tr>
  );
};

/* ── Мобильная карточка киоска ───────────────────────────────────────────── */
const MobileDeviceCard = ({ device, onOpen }) => {
  const printerOk = device.printer_status === 'ok' || device.printer_status === 'Ready';
  const isPending = device.status === 'pending';

  return (
    <div className="card" style={{ padding: '14px', marginBottom: '10px', opacity: device.is_blocked ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
        <div style={{ minWidth: 0 }}><DeviceTitle device={device} /></div>
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <DeviceStatusBadge device={device} />
          <div style={{ fontSize: '11px', marginTop: '2px' }} className="text-dim">{fmtDate(device.last_seen)}</div>
        </div>
      </div>

      {!isPending && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap',
          padding: '8px 0 10px', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: '12px',
        }}>
          <div>
            <span style={{ color: printerOk ? 'var(--green)' : device.printer_status ? 'var(--yellow)' : 'var(--text-3)' }}>
              🖨 {device.printer_status || '—'}
            </span>
            <span style={{ color: device.paper_low ? 'var(--yellow)' : 'var(--text-3)', marginLeft: '10px' }}>
              <Layers size={9} style={{ verticalAlign: '-1px' }} /> {device.paper_count > 0 ? `${fmt(device.paper_count)} л.` : '—'}
            </span>
          </div>
          <div>
            <span style={{ fontWeight: '600' }}>{fmt(device.revenue_30d)}</span>
            <span className="text-dim" style={{ fontSize: '11px' }}> UZS</span>
            <span className="text-dim" style={{ fontSize: '11px' }}> · {fmt(device.orders_30d)} зак.</span>
          </div>
        </div>
      )}

      <ManageButton device={device} onOpen={onOpen} mobile />
    </div>
  );
};

/* ── Delete modal ────────────────────────────────────────────────────────── */
const DeleteModal = ({ device, onConfirm, onCancel }) => (
  <div className="modal-overlay" style={{ zIndex: 1100, padding: '24px 12px', overflowY: 'auto' }}>
    <div className="card" style={{ width: '360px', maxWidth: '94vw', padding: '28px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--red)' }}>
        <AlertCircle size={17} /> Удалить киоск?
      </h3>
      <p style={{ fontSize: '13px', marginBottom: '6px' }}>
        Устройство: <strong>{device.name || device.machine_name || device.device_id}</strong>
      </p>
      <p className="text-dim" style={{ fontSize: '12px', marginBottom: '24px' }}>
        Будут удалены все заказы и сессии этого устройства. Действие необратимо.
      </p>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onCancel}>Отмена</button>
        <button className="btn btn-danger" onClick={() => onConfirm(device.device_id)}>
          <Trash2 size={13} /> Удалить
        </button>
      </div>
    </div>
  </div>
);

/* ── Create device modal ─────────────────────────────────────────────────── */
const CreateDeviceModal = ({ onConfirm, onCancel }) => {
  const [name, setName]           = useState('');
  const [location, setLocation]   = useState('');
  const [coords, setCoords]       = useState(null); // { lat, lng } | null
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Введите название киоска'); return; }
    setLoading(true);
    setError('');
    try {
      await onConfirm({
        name: name.trim(),
        location: location.trim() || undefined,
        lat: coords?.lat,
        lng: coords?.lng,
      });
    } catch (err) {
      setError(err.message || 'Ошибка создания');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ overflowY: 'auto', padding: '24px 12px' }}>
      <div className="card" style={{ width: '480px', maxWidth: '94vw', padding: '28px', maxHeight: '92vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '20px' }}>
          Добавить киоск
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' }}>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--text-3)', display: 'block', marginBottom: '5px' }}>
              Название <span style={{ color: 'var(--red)' }}>*</span>
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Например: ТЦ Афросиаб — 1 этаж"
              style={{ width: '100%' }}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--text-3)', display: 'block', marginBottom: '5px' }}>Местоположение</label>
            <input
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Адрес или описание места"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Точка на карте — где киоск должен стоять (для карты и антивора) */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-3)', display: 'block', marginBottom: '6px' }}>
            Точка на карте
          </label>
          <MapPicker value={coords} onChange={setCoords} height={220} />
        </div>

        {error && (
          <p style={{ fontSize: '12px', color: 'var(--red)', marginBottom: '14px' }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel} disabled={loading}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            <Plus size={13} /> {loading ? 'Создание...' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Activation code modal ───────────────────────────────────────────────── */
const ActivationCodeModal = ({ deviceName, code, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1101, background: 'rgba(0,0,0,0.75)', padding: '24px 12px', overflowY: 'auto' }}>
      <div className="card" style={{ width: '400px', maxWidth: '94vw', padding: '32px', textAlign: 'center' }}>
        <CheckCircle size={40} style={{ color: 'var(--green)', margin: '0 auto 16px' }} />
        <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>
          Киоск создан
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '24px' }}>
          {deviceName}
        </p>

        <p style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '10px' }}>
          Код активации:
        </p>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '14px 20px',
          marginBottom: '18px',
        }}>
          <span style={{ fontSize: '28px', fontFamily: 'Consolas, monospace', fontWeight: '700', letterSpacing: '6px', color: 'var(--green)' }}>
            {code}
          </span>
          <button
            className="btn-icon"
            title={copied ? 'Скопировано!' : 'Копировать'}
            onClick={handleCopy}
            style={{ color: copied ? 'var(--green)' : 'var(--text-3)' }}
          >
            <Copy size={16} />
          </button>
        </div>

        <div style={{
          background: 'rgba(255, 180, 0, 0.08)', border: '1px solid rgba(255, 180, 0, 0.3)',
          borderRadius: '8px', padding: '10px 14px', marginBottom: '24px',
        }}>
          <p style={{ fontSize: '12px', color: 'var(--yellow)', textAlign: 'left', lineHeight: '1.5' }}>
            ⚠ Сохраните код — он больше не будет показан. После ввода в лаунчере код становится недействительным.
          </p>
        </div>

        <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>
          Понятно
        </button>
      </div>
    </div>
  );
};

/* ── Main ────────────────────────────────────────────────────────────────── */
export const Devices = () => {
  const { fetchAuthorized, isAdmin } = useAuth();
  const [devices, setDevices]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [activeId, setActiveId]         = useState(null);   // device_id открытой модалки
  const [toDelete, setToDelete]         = useState(null);
  const [showCreate, setShowCreate]     = useState(false);
  const [activationInfo, setActivationInfo] = useState(null); // { name, code }
  const [toasts, setToasts]             = useState([]);
  const toastId = useRef(0);

  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetchAuthorized('/api/dashboard/devices');
      const data = await res.json();
      if (data.success) setDevices(data.devices);
    } catch { addToast('Ошибка загрузки устройств', 'error'); }
    finally { setLoading(false); }
  }, [fetchAuthorized, addToast]);

  const handleWs = useCallback((msg) => {
    if (msg.event === 'device_online') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: true, last_seen: new Date().toISOString() } : d
      ));
    }
    if (msg.event === 'device_offline') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: false } : d
      ));
    }
    if (msg.event === 'device_telemetry') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id
          ? {
              ...d,
              is_online:      true,
              last_seen:      new Date().toISOString(),
              printer_status: msg.data.printer_status ?? d.printer_status,
              paper_count:    msg.data.paper_count != null ? msg.data.paper_count : d.paper_count,
              paper_low:      msg.data.paper_count != null && d.low_paper_threshold > 0
                                ? msg.data.paper_count > 0 && msg.data.paper_count <= d.low_paper_threshold
                                : d.paper_low,
            }
          : d
      ));
    }
  }, []);

  useAdminWs(handleWs);
  useEffect(() => { loadDevices(); }, [loadDevices]);

  const handleDelete = async (deviceId) => {
    try {
      const res = await fetchAuthorized(`/api/dashboard/devices/${deviceId}`, { method: 'DELETE' });
      if (res.ok) {
        setDevices(prev => prev.filter(d => d.device_id !== deviceId));
        addToast('Киоск удалён', 'success');
        setActiveId(null);
      } else {
        addToast('Ошибка при удалении', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
    setToDelete(null);
  };

  const handlePaymentToggle = async (deviceId) => {
    try {
      const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/payment-toggle`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, payment_enabled: data.payment_enabled } : d));
        addToast(`Оплата ${data.payment_enabled ? 'включена' : 'выключена'}`, 'success');
      } else {
        addToast('Ошибка переключения оплаты', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
  };

  const handleBlock = async (deviceId) => {
    try {
      const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/block`, { method: 'PUT' });
      const data = await res.json();
      if (data.success) {
        setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, is_blocked: 1 } : d));
        addToast('Киоск заблокирован', 'success');
      } else {
        addToast('Ошибка блокировки', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
  };

  const handleUnblock = async (deviceId) => {
    try {
      const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/unblock`, { method: 'PUT' });
      const data = await res.json();
      if (data.success) {
        setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, is_blocked: 0 } : d));
        addToast('Киоск разблокирован', 'success');
      } else {
        addToast('Ошибка разблокировки', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
  };

  const handleResetWs = async (deviceId) => {
    try {
      const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/reset-ws`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        // ws_sent=true → команда долетела сразу через WS; иначе подождёт телеметрии
        if (data.ws_sent) addToast('Сброс WS отправлен по WS-каналу', 'success');
        else              addToast('Флаг сброса WS поставлен (применится в ≤ интервал телеметрии)', 'success');
      } else {
        addToast('Ошибка отправки', 'error');
      }
    } catch { addToast('Ошибка соединения', 'error'); }
  };

  const handleSavePrices = async (deviceId, payload) => {
    const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/prices`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Ошибка сохранения цен');
    setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, ...data.prices } : d));
    addToast('Цены киоска обновлены', 'success');
  };

  const handleSaveLocation = async (deviceId, coords) => {
    const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/location`, {
      method: 'POST',
      body: JSON.stringify(coords ? { lat: coords.lat, lng: coords.lng } : { lat: null, lng: null }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Ошибка сохранения локации');
    setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, lat: data.lat, lng: data.lng } : d));
    addToast(coords ? 'Точка киоска сохранена' : 'Точка киоска удалена', 'success');
  };

  const handleSaveInfo = async (deviceId, fields) => {
    const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/info`, {
      method: 'POST',
      body: JSON.stringify(fields),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Ошибка сохранения');
    setDevices(prev => prev.map(d => d.device_id === deviceId
      ? { ...d, name: data.name, location: data.location }
      : d));
    addToast('Данные киоска обновлены', 'success');
  };

  const handleSaveAnyDesk = async (deviceId, fields) => {
    const res  = await fetchAuthorized(`/api/dashboard/devices/${deviceId}/anydesk`, {
      method: 'POST',
      body: JSON.stringify(fields),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Ошибка сохранения');
    setDevices(prev => prev.map(d => d.device_id === deviceId
      ? { ...d, anydesk_id: data.anydesk_id, anydesk_password: data.anydesk_password }
      : d));
    addToast('Данные AnyDesk сохранены', 'success');
  };

  const handleCreateDevice = async ({ name, location, lat, lng }) => {
    const res  = await fetchAuthorized('/api/dashboard/devices/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, location, lat, lng }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Ошибка создания');

    setShowCreate(false);
    setActivationInfo({ name, code: data.activation_code });
    await loadDevices();
  };

  const modalHandlers = {
    onPaymentToggle: handlePaymentToggle,
    onBlock:         handleBlock,
    onUnblock:       handleUnblock,
    onResetWs:       handleResetWs,
    onSaveInfo:      handleSaveInfo,
    onSavePrices:    handleSavePrices,
    onSaveAnyDesk:   handleSaveAnyDesk,
    onSaveLocation:  handleSaveLocation,
    onRequestDelete: setToDelete,
  };

  const filtered = devices.filter(d =>
    (d.device_id   || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.machine_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.name         || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.location     || '').toLowerCase().includes(search.toLowerCase())
  );

  // Всегда берём актуальную версию из devices, чтобы модалка отражала свежее
  // состояние после оплаты/блокировки/правок без ручной синхронизации.
  const activeDevice = activeId ? devices.find(d => d.device_id === activeId) : null;

  return (
    <div className="fade-in">
      {/* Toasts */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))} />
        ))}
      </div>

      {activeDevice && <DeviceModal device={activeDevice} handlers={modalHandlers} onClose={() => setActiveId(null)} />}
      {toDelete && <DeleteModal device={toDelete} onConfirm={handleDelete} onCancel={() => setToDelete(null)} />}
      {showCreate && <CreateDeviceModal onConfirm={handleCreateDevice} onCancel={() => setShowCreate(false)} />}
      {activationInfo && (
        <ActivationCodeModal
          deviceName={activationInfo.name}
          code={activationInfo.code}
          onClose={() => setActivationInfo(null)}
        />
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Киоски</h1>
          <p>Мониторинг и управление устройствами</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
            <input
              placeholder="Поиск..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '200px', paddingLeft: '32px' }}
            />
          </div>
          <button className="btn-icon" onClick={loadDevices} disabled={loading} title="Обновить">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
          {isAdmin && (
            <button
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={14} /> Добавить киоск
            </button>
          )}
        </div>
      </div>

      {/* Table — десктоп */}
      <div className="card desktop-only" style={{ overflow: 'hidden' }}>
        <table>
          <thead>
            <tr>
              <th>Устройство</th>
              <th>Статус</th>
              <th>Принтер</th>
              <th>Доход / 30 дней</th>
              <th style={{ width: '150px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map(d => (
                <DeviceRow key={d.device_id} device={d} onOpen={(dev) => setActiveId(dev.device_id)} />
              ))
            ) : (
              <tr>
                <td colSpan={5} className="empty">
                  {loading ? 'Загрузка...' : 'Устройства не найдены'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Карточки — мобильная версия той же таблицы */}
      <div className="mobile-only">
        {filtered.length > 0 ? (
          filtered.map(d => (
            <MobileDeviceCard key={d.device_id} device={d} onOpen={(dev) => setActiveId(dev.device_id)} />
          ))
        ) : (
          <div className="card empty" style={{ padding: '32px' }}>
            {loading ? 'Загрузка...' : 'Устройства не найдены'}
          </div>
        )}
      </div>
    </div>
  );
};
